<template>
  <AccountContextWrapper>
    <template #default>
      <section class="budget-templates-index mt-3">
        <div class="text-start mb-3">
          <h2 class="h5 mb-2">Budget Templates</h2>
          <p class="mb-0 text-muted">
            Create and manage reusable budget templates that can seed future
            budget periods.
          </p>
        </div>

        <bPanel
          title="Budget Templates"
          tag="article"
          class="border"
          max-width="48rem"
        >
          <div
            class="d-flex justify-content-between align-items-center mb-3 gap-2"
          >
            <p class="mb-0 text-muted">
              Select a template to review, then edit it if needed.
            </p>
            <BButton variant="primary" @click="goToCreate">
              Create Template
            </BButton>
          </div>

          <div v-if="loading" class="text-center my-4">
            <BSpinner label="Loading templates..." />
          </div>

          <div v-else-if="hasError" class="text-center my-4 text-danger">
            <IconAlertTriangle class="me-1" />
            Unable to load budget templates.
            <div class="mt-2">
              <BButton variant="outline-danger" size="sm" @click="retryFetch">
                Retry
              </BButton>
            </div>
          </div>

          <div v-else-if="templates.length === 0" class="text-center my-4">
            <IconAlertTriangle class="me-1 text-warning" />
            No budget templates found for this account.
          </div>

          <div v-else>
            <BFormGroup
              label="Available Templates"
              label-for="budget-template-selector"
              class="mb-3"
            >
              <BFormSelect
                id="budget-template-selector"
                v-model="selectedTemplateUid"
                :options="templateOptions"
              />
            </BFormGroup>

            <div
              v-if="selectedTemplate"
              class="selected-template border rounded p-3"
            >
              <div
                class="d-flex justify-content-between align-items-center mb-2 gap-2"
              >
                <h3 class="h6 mb-0">{{ selectedTemplate.name }}</h3>
                <BButton
                  variant="outline-secondary"
                  size="sm"
                  @click="goToEdit"
                >
                  Edit
                </BButton>
              </div>
              <small class="text-muted d-block mb-2">
                {{ selectedTemplate.categories?.length || 0 }} categories
                configured.
              </small>
              <ul class="list-group">
                <li
                  v-for="category in selectedTemplate.categories || []"
                  :key="`${selectedTemplate.template_uid}-${category.category_uid}`"
                  class="list-group-item d-flex justify-content-between"
                >
                  <span>{{ category.name }}</span>
                  <strong
                    >${{ Number(category.budget_amount).toFixed(2) }}</strong
                  >
                </li>
              </ul>
            </div>
          </div>

          <div
            v-if="spendingCategoriesWarning"
            class="alert alert-warning mt-3 mb-0"
          >
            Spending categories could not be loaded. Create/edit forms may have
            limited options.
          </div>
        </bPanel>
      </section>
    </template>
  </AccountContextWrapper>
</template>

<script>
import { BButton, BFormGroup, BFormSelect, BSpinner } from "bootstrap-vue-next";
import { IconAlertTriangle } from "@tabler/icons-vue";
import bPanel from "@/components/atoms/bPanel.vue";
import AccountContextWrapper from "@/components/molecules/AccountContextWrapper.vue";
import {
  budgetTemplates as budgetTemplatesNs,
  userAccount as userAcctNs,
  spendingCategory as spendingCategoryNs,
} from "@/store/namespaces";

export default {
  name: "BudgetTemplatesIndexView",
  components: {
    AccountContextWrapper,
    BButton,
    BFormGroup,
    BFormSelect,
    BSpinner,
    IconAlertTriangle,
    bPanel,
  },
  data() {
    return {
      selectedTemplateUid: null,
      spendingCategoriesWarning: false,
    };
  },
  computed: {
    acctUid() {
      return this.$store.getters[`${userAcctNs}/getSelectedAcctUid`];
    },
    templates() {
      return this.$store.getters[`${budgetTemplatesNs}/getTemplatesByAcctUid`](
        this.acctUid
      );
    },
    loading() {
      return this.$store.getters[`${budgetTemplatesNs}/getLoadingByAcctUid`](
        this.acctUid
      );
    },
    hasError() {
      return this.$store.getters[`${budgetTemplatesNs}/getErrorByAcctUid`](
        this.acctUid
      );
    },
    templateOptions() {
      const templateOptions = this.templates.map((template) => ({
        value: template.template_uid,
        text: template.name,
      }));

      return [{ value: null, text: "Select a template" }, ...templateOptions];
    },
    selectedTemplate() {
      if (!this.selectedTemplateUid) {
        return null;
      }

      return (
        this.templates.find(
          (template) => template.template_uid === this.selectedTemplateUid
        ) || null
      );
    },
  },
  watch: {
    acctUid: {
      immediate: true,
      async handler(nextAcctUid) {
        if (!nextAcctUid) {
          return;
        }

        await this.fetchTemplates(nextAcctUid);
      },
    },
    templates: {
      immediate: true,
      handler(templates) {
        if (!templates || templates.length === 0) {
          this.selectedTemplateUid = null;
          return;
        }

        const selectedStillExists = templates.some(
          (template) => template.template_uid === this.selectedTemplateUid
        );

        if (!selectedStillExists) {
          this.selectedTemplateUid = templates[0].template_uid;
        }
      },
    },
  },
  async created() {
    await this.fetchSpendingCategories();
  },
  methods: {
    async fetchTemplates(acctUid, force = false) {
      await this.$store.dispatch(
        `${budgetTemplatesNs}/fetchBudgetTemplatesByAcctUid`,
        {
          acctUid,
          force,
        }
      );
    },
    async fetchSpendingCategories() {
      try {
        // This method already handles caching internally, so we can call it without checking if we already have categories
        const params = { sort: "name:asc" };
        await this.$store.dispatch(
          `${spendingCategoryNs}/fetchSpendingCategories`,
          params
        );
        this.spendingCategoriesWarning = false;
      } catch (error) {
        console.error("Error fetching spending categories:", error);
        this.spendingCategoriesWarning = true;
      }
    },
    retryFetch() {
      if (!this.acctUid) {
        return;
      }

      this.fetchTemplates(this.acctUid, true);
    },
    goToCreate() {
      this.$router.push({ path: "/budget/templates/create" });
    },
    goToEdit() {
      if (!this.selectedTemplateUid) {
        return;
      }

      this.$router.push({
        path: `/budget/templates/${this.selectedTemplateUid}/edit`,
      });
    },
  },
};
</script>

<style scoped>
.budget-templates-index {
  width: min(100%, 48rem);
}
</style>
